import React, { useCallback, useEffect, useMemo, useState } from 'react';
import s from './s.module.less';
import useGetProviderAppointmentList from 'hooks/useGetProviderAppointmentList';
import { message, Spin } from 'antd';
import { EServiceType, Prospect, TAppointmentItem } from 'types/common';
import { getProviderAppointmentListCount } from 'api/inbox';
import { getGPAppointmentCounts } from 'api/group';
import useGetProviderTextVisitList from 'hooks/useGetProviderTextVisitList';
import { CaseListItem } from 'types/textVisit';
import dayjs from 'utils/dayjs';
// eslint-disable-next-line import/no-cycle
import ListContent from './components/ListContent';

export interface AppointmentItem {
    status?: string;
    title: string;
    date: number;
    time: number;
    duration: number;
    teleHealthLink: string;
    visitReason: string;
    onView?: () => void;
}

interface AppointmentListProps {
    title?: string;
    prospect?: Prospect;
    forceReset?: {};
    smallSize?: boolean;
    showCreateVisitAction?: boolean;
    isInTextVisitPop?: boolean;
    isGroupPractice?: boolean;
    useInModule?: boolean;
}

const AppointmentList = ({
    title,
    prospect,
    smallSize = false,
    showCreateVisitAction,
    isInTextVisitPop,
    isGroupPractice,
    useInModule,
}: AppointmentListProps) => {
    const [activeTab, setActiveTab] = useState<'InProgress' | 'PastDue' | 'Upcoming' | 'Past'>('InProgress');
    const [upcomingCount, setUpcomingCount] = useState(0);
    const [pastCount, setPastCount] = useState(0);
    const [inProgressCount, setInProgressCount] = useState(0);
    const [pastDueCount, setPastDueCount] = useState(0);
    const clientId = isGroupPractice ? prospect?.identityId : prospect?.id;
    const [countsLoading, setCountsLoading] = useState(false);
    const [countsReady, setCountsReady] = useState(false);

    const fetchCountsByType = useCallback(async () => {
        if (!clientId) {
            return;
        }

        setCountsLoading(true);
        try {
            const res = isGroupPractice
                ? await getGPAppointmentCounts(clientId)
                : await getProviderAppointmentListCount(clientId);

            if (res && !res.error) {
                const data = res?.data?.data || {
                    pastSize: 0,
                    upcomingSize: 0,
                    inProgressSize: 0,
                    pastDueSize: 0,
                };
                setUpcomingCount(data.upcomingSize);
                setPastCount(data.pastSize);
                setInProgressCount(data.inProgressSize);
                setPastDueCount(data.pastDueSize);
            } else {
                message.error(res?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCountsLoading(false);
            setCountsReady(true);
        }
    }, [clientId, isGroupPractice]);

    // const currentData = useMemo(() => {
    //     if (activeTab === 'Upcoming') {
    //         return upcomingAppointmentList;
    //     }
    //     return pastAppointmentList;
    // }, [activeTab, upcomingAppointmentList, pastAppointmentList]);

    const mergeListByTime = useCallback((videoList: TAppointmentItem[], textVisitList: CaseListItem[]) => {
        return [...videoList, ...textVisitList].sort((a: any, b: any) =>
            dayjs(b.createdAt || b.assignedProviderAt).diff(dayjs(a.createdAt || a.assignedProviderAt)),
        );
    }, []);

    const [videoList, videoLoading, fetchVideoList] = useGetProviderAppointmentList({
        current: 1,
        size: 500,
        type: activeTab as 'Upcoming' | 'Past',
        clientId,
        autoFetch: false,
        isPracticeView: isGroupPractice,
    });

    // Keep InProgress text visits always available so "Past due" tab visibility is stable.
    const [inProgressTextVisitList, inProgressTextVisitLoading, fetchInProgressTextVisitList] =
        useGetProviderTextVisitList({
            current: 1,
            size: 500,
            type: 'InProgress',
            clientId: clientId!,
            autoFetch: false,
            isPracticeView: isGroupPractice,
        });

    const [pastTextVisitList, pastTextVisitLoading, fetchPastTextVisitList] = useGetProviderTextVisitList({
        current: 1,
        size: 500,
        type: 'Past',
        clientId: clientId!,
        autoFetch: false,
        isPracticeView: isGroupPractice,
    });

    const [upcomingTextVisitList, upcomingTextVisitLoading, fetchUpcomingTextVisitList] = useGetProviderTextVisitList({
        current: 1,
        size: 500,
        type: 'Upcoming',
        clientId: clientId!,
        autoFetch: false,
        isPracticeView: isGroupPractice,
    });

    const [pastDueTextVisitList, pastDueTextVisitLoading, fetchPastDueTextVisitList] = useGetProviderTextVisitList({
        current: 1,
        size: 500,
        type: 'PastDue',
        clientId: clientId!,
        autoFetch: false,
        isPracticeView: isGroupPractice,
    });

    const currentPreviewData = useMemo(() => {
        if (activeTab === 'Upcoming') {
            return mergeListByTime(videoList, upcomingTextVisitList);
        } else if (activeTab === 'InProgress') {
            return inProgressTextVisitList;
        } else if (activeTab === 'PastDue') {
            return pastDueTextVisitList;
        } else {
            return mergeListByTime(videoList, pastTextVisitList);
        }
    }, [
        activeTab,
        videoList,
        upcomingTextVisitList,
        pastTextVisitList,
        pastDueTextVisitList,
        inProgressTextVisitList,
        mergeListByTime,
    ]);

    useEffect(() => {
        if (clientId) {
            fetchCountsByType();
            fetchInProgressTextVisitList(1, 500, 'InProgress', clientId);
        }
    }, [clientId, fetchCountsByType, fetchInProgressTextVisitList]);

    useEffect(() => {
        if (!clientId) return;

        if (activeTab === 'Upcoming') {
            fetchVideoList(1, 500, 'Upcoming', clientId);
            fetchUpcomingTextVisitList(1, 500, 'Upcoming', clientId);
            return;
        }

        if (activeTab === 'Past') {
            fetchVideoList(1, 500, 'Past', clientId);
            fetchPastTextVisitList(1, 500, 'Past', clientId);
            return;
        }

        if (activeTab === 'PastDue') {
            fetchPastDueTextVisitList(1, 500, 'PastDue', clientId);
            return;
        }

        fetchInProgressTextVisitList(1, 500, 'InProgress', clientId);
    }, [
        activeTab,
        clientId,
        fetchInProgressTextVisitList,
        fetchPastDueTextVisitList,
        fetchPastTextVisitList,
        fetchVideoList,
        fetchUpcomingTextVisitList,
    ]);

    // useEffect(() => {
    //     if (activeTab === 'Upcoming') {
    //         setShowMoreButton(upcomingAppointmentList.length < upcomingCount);
    //     } else {
    //         setShowMoreButton(pastAppointmentList.length < pastCount);
    //     }
    // }, [activeTab, upcomingAppointmentList, pastAppointmentList, upcomingCount, pastCount]);

    return (
        <div className={s.listWrapper}>
            <Spin
                spinning={
                    countsLoading ||
                    videoLoading ||
                    inProgressTextVisitLoading ||
                    pastDueTextVisitLoading ||
                    pastTextVisitLoading ||
                    upcomingTextVisitLoading
                }
            >
                {title && <div className={s.title}>{title}</div>}
                <ListContent
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    data={currentPreviewData}
                    showMoreButton={false}
                    client={prospect}
                    countsReady={countsReady}
                    upcomingCount={upcomingCount}
                    pastCount={pastCount}
                    inProgressCount={inProgressCount}
                    pastDueCount={pastDueCount}
                    smallSize={smallSize}
                    showCreateVisitAction={showCreateVisitAction}
                    clientId={prospect?.id}
                    isGroupPractice={isGroupPractice}
                    isInTextVisitPop={isInTextVisitPop}
                    useInModule={useInModule}
                    onUpdate={() => {
                        fetchInProgressTextVisitList(1, 500, 'InProgress', clientId);
                        fetchPastDueTextVisitList(1, 500, 'PastDue', clientId);
                        fetchPastTextVisitList(1, 500, 'Past', clientId);
                        fetchUpcomingTextVisitList(1, 500, 'Upcoming', clientId);
                        fetchVideoList(1, 500, activeTab === 'Upcoming' ? 'Upcoming' : 'Past', clientId);
                        fetchCountsByType();
                    }}
                    onVisitCreated={(serviceType?: EServiceType) => {
                        if (serviceType === EServiceType.PRESCRIPTION_SERVICE) {
                            setActiveTab('InProgress');
                        } else {
                            setActiveTab('Upcoming');
                        }
                    }}
                />
            </Spin>
        </div>
    );
};

export default AppointmentList;
